import { create } from 'zustand'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import type { SidePanel } from '~publish/components/Composer/store/appSlice'
import { toggleAIAssistant } from '~publish/legacy/ai/state/slice'
import { isRpcUpdate } from '~publish/legacy/post/helpers'
import type { RpcUpdate } from '~publish/legacy/post/types'
import { selectCurrentOrganizationId } from '~publish/legacy/organizations/selectors'
import { selectProfiles } from '~publish/legacy/profile-sidebar/selectors'
import { dispatch, store } from '~publish/legacy/store'
import getCtaProperties from '~publish/legacy/analytics-middleware/utils/CtaStrings'
import callRpc from '~publish/legacy/utils/call-rpc'
import type { ExtendedIdea } from '~publish/pages/Create/types'

import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

/**
 * The subset of `PublishingPost` this store reads. Only `serviceLink` is
 * used, so it is derived from a colocated fragment. The fragment exists only
 * to derive the type — it is not spread into a query.
 */
const ComposerDialogStore_PublishingPost = graphql(`
  fragment ComposerDialogStore_PublishingPost on PublishingPost {
    serviceLink
  }
`)
type PublishingPost = ResultOf<typeof ComposerDialogStore_PublishingPost>

export type PrefillPostData = Partial<
  Omit<RpcUpdate, 'tags'> & { tags?: string[] }
>

export interface ComposerDialogState {
  showComposer: boolean
  /**
   * Latches true the first time the composer opens and never resets —
   * unlike `showComposer`, which toggles closed again. Gates the lazy
   * import of `ComposerDialog` so its chunk loads on first open rather
   * than on every app mount, without unmounting (and losing) the dialog's
   * listeners on subsequent closes.
   */
  hasEverOpened: boolean
  returnFocusTo?: HTMLElement | null
  editMode: boolean
  duplicatedFrom: string | null
  emptySlotMode: boolean
  hasDefaultTime: boolean
  editingPostId?: string
  /**
   * The ContentItem/PostGroup this session's post belongs to, whether that's
   * a channel-less draft or a channel-specific post that's part of a group.
   * Seeds `omni.draftId`: for a channel-less draft, Save routes through it to
   * updateContentItemDraft; either way, it's the id the Header's title editor
   * mutates against.
   */
  contentItemId?: string
  /** The ContentItem's `title`, alongside `contentItemId`; seeds `omni.title`. */
  contentItemTitle?: string | null
  selectedProfileId?: string
  selectedProfileIds: string[]
  post?: RpcUpdate
  prefillPostData?: PrefillPostData
  splits: Record<string, boolean>
  withAIPanelOpen: boolean
  draftMode: boolean
  preSelectedTags: string[]
  sentPost: boolean
  idea?: ExtendedIdea
  templateId?: string
  cta?: string
  activeSidePanel?: SidePanel
  prefillAiPrompt?: string | null
}

export type OpenComposerInput = Partial<
  Omit<ComposerDialogState, 'showComposer' | 'splits'>
>

export interface ComposerDialogActions {
  openComposer: (input: OpenComposerInput) => void
  handleCreatePostClick: (
    input: Pick<
      OpenComposerInput,
      | 'selectedProfileId'
      | 'selectedProfileIds'
      | 'cta'
      | 'prefillPostData'
      | 'hasDefaultTime'
      | 'withAIPanelOpen'
      | 'idea'
    >,
  ) => void
  hideComposer: (isDiscarded?: boolean) => void
  handleEditClick: (
    postId: string,
    options?: {
      cta?: string
      withAIPanelOpen?: boolean
      draftMode?: boolean
      contentItemId?: string
      contentItemTitle?: string | null
      /** Loads the post to edit. Defaults to the `getPost` RPC. */
      fetchPost?: (postId: string) => Promise<unknown>
    },
  ) => Promise<void>
  handleShareLinkClick: (
    post: RpcUpdate | PublishingPost,
    selectedProfileIds: string[],
  ) => void
  handleOpenComposerFromQueryParams: (
    input: Pick<
      OpenComposerInput,
      'selectedProfileIds' | 'cta' | 'post' | 'hasDefaultTime'
    >,
  ) => void
  addSplit: (splits: Record<string, boolean>) => void
}

export type ComposerDialogStore = ComposerDialogState & ComposerDialogActions

export const initialComposerDialogState: ComposerDialogState = {
  showComposer: false,
  hasEverOpened: false,
  editMode: false,
  duplicatedFrom: null,
  emptySlotMode: false,
  hasDefaultTime: false,
  editingPostId: undefined,
  contentItemId: undefined,
  contentItemTitle: undefined,
  selectedProfileId: undefined, // Provided when in the context of a single profile (e.g. queue)
  selectedProfileIds: [], // Provided when in the context of multiple profiles (e.g. calendar)
  post: undefined,
  prefillPostData: undefined,
  splits: {},
  withAIPanelOpen: false,
  draftMode: false,
  preSelectedTags: [],
  sentPost: false,
  idea: undefined,
  templateId: undefined,
  cta: undefined,
  activeSidePanel: undefined,
  prefillAiPrompt: undefined,
}

/**
 * Fires the "composer opened" tracking event and, when requested, opens the
 * AI Assistant panel. Both read/write Redux directly since this store is
 * the only thing that knows a composer open just happened.
 */
function runOpenComposerSideEffects(input: OpenComposerInput): void {
  const state = store.getState()
  const organizationId = selectCurrentOrganizationId(state)
  if (input.cta && organizationId) {
    const ctaProperties = getCtaProperties(input.cta)
    BufferTracker.composerOpened({
      product: 'publish',
      clientName: 'publishWeb',
      organizationId,
      ...ctaProperties,
      channelsConnected: selectProfiles(state).length,
      channelsSelected: input.selectedProfileIds?.length || 0,
    })
  }

  if (input.withAIPanelOpen) {
    dispatch(toggleAIAssistant(true))
  }
}

// Tracks the most recently issued handleEditClick call so a slower-to-resolve
// getPost request can't win the race and act on stale data — whether it lost
// to a newer edit click, or the composer session moved on for any other
// reason (opened something else, or closed) before it resolved.
let latestEditRequestId = 0

export const useComposerDialogStore = create<ComposerDialogStore>(
  (set, get) => ({
    ...initialComposerDialogState,

    openComposer: (input): void => {
      // Any new session — including one opened by something other than
      // handleEditClick — invalidates whatever edit request was in flight,
      // so its stale response can't overwrite the session opened here.
      latestEditRequestId++
      set({
        showComposer: true,
        hasEverOpened: true,
        returnFocusTo: input.returnFocusTo,
        post: input.post,
        prefillPostData: input.prefillPostData,
        draftMode: input.draftMode ?? false,
        editMode: input.editMode ?? false,
        duplicatedFrom: input.duplicatedFrom ?? null,
        editingPostId: input.editingPostId,
        contentItemId: input.contentItemId,
        contentItemTitle: input.contentItemTitle,
        emptySlotMode: input.emptySlotMode ?? false,
        hasDefaultTime: input.hasDefaultTime ?? false,
        selectedProfileId: input.selectedProfileId,
        selectedProfileIds: input.selectedProfileIds ?? [],
        preSelectedTags: input.preSelectedTags ?? [],
        idea: input.idea,
        templateId: input.templateId,
        cta: input.cta,
        activeSidePanel: input.activeSidePanel,
        prefillAiPrompt: input.prefillAiPrompt,
      })
      runOpenComposerSideEffects(input)
    },

    handleCreatePostClick: (input): void => {
      get().openComposer({
        selectedProfileId: input.selectedProfileId,
        selectedProfileIds: input.selectedProfileIds,
        cta: input.cta,
        prefillPostData: input.prefillPostData,
        hasDefaultTime: input.hasDefaultTime ?? false,
        withAIPanelOpen: input.withAIPanelOpen ?? false,
        idea: input.idea,
      })
    },

    hideComposer: (): void => {
      // Closing the composer must also invalidate an in-flight edit request —
      // otherwise its stale response could reopen the composer the user just
      // dismissed.
      latestEditRequestId++
      set({
        showComposer: false,
        returnFocusTo: undefined,
        editMode: false,
        emptySlotMode: false,
        hasDefaultTime: false,
        selectedProfileId: undefined,
        selectedProfileIds: [],
        post: undefined,
        prefillPostData: undefined,
        draftMode: false,
        preSelectedTags: [],
        idea: undefined,
        templateId: undefined,
        contentItemId: undefined,
        contentItemTitle: undefined,
        cta: undefined,
        activeSidePanel: undefined,
        prefillAiPrompt: undefined,
      })
    },

    handleEditClick: async (postId, options): Promise<void> => {
      const requestId = ++latestEditRequestId
      let post: unknown
      try {
        post = options?.fetchPost
          ? await options.fetchPost(postId)
          : await callRpc('getPost', { updateId: postId })
      } catch {
        if (requestId === latestEditRequestId) {
          toast.error('Failed to load post for editing')
        }
        return
      }
      // Superseded by a newer edit click, or the composer session moved on
      // (opened something else, or closed) while this was in flight — either
      // way this response is stale, so don't act on it.
      if (requestId !== latestEditRequestId) return
      if (!isRpcUpdate(post)) {
        toast.error('Failed to load post for editing')
        return
      }

      get().openComposer({
        selectedProfileIds: [post.profileId],
        post,
        editingPostId: post.id,
        editMode: true,
        cta: options?.cta,
        withAIPanelOpen: options?.withAIPanelOpen,
        draftMode: options?.draftMode,
        contentItemId: options?.contentItemId,
        contentItemTitle: options?.contentItemTitle,
      })
    },

    handleShareLinkClick: (post, selectedProfileIds): void => {
      get().openComposer({
        selectedProfileIds,
        // prefillPostData (not post/initialPost) is the field meant to carry
        // a partial seed — post is a full RpcUpdate reserved for edit mode.
        prefillPostData: { text: `${post.serviceLink}` },
      })
    },

    handleOpenComposerFromQueryParams: (input): void => {
      get().openComposer({
        selectedProfileIds: input.selectedProfileIds,
        cta: input.cta,
        post: input.post,
        hasDefaultTime: input.hasDefaultTime ?? false,
      })
    },

    addSplit: (splits): void => {
      set((state) => ({
        splits: {
          ...state.splits,
          ...splits,
        },
      }))
    },
  }),
)
